import { useKeycloak } from '@react-keycloak/web';
import type { AxiosInstance, AxiosResponse } from 'axios';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToasts } from 'react-toast-notifications';
import { CompanySettings } from '../types/settings';

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

export const useCompanySettings = () => {
    const [settings, setSettings] = useState<CompanySettings>();
    const axios = useAxios();
    const { addToast } = useToasts();

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }

        axios.current
            .get<CompanySettings>('/company/settings')
            .then(({ data }) => {
                setSettings(data);
            })
            .catch((error) => {
                addToast(error.message, { appearance: 'error' });
            });
    }, [addToast, axios]);

    return settings;
};

export const useAxios = (baseURL: string = API_BASE_URL) => {
    const axiosInstance = useRef<AxiosInstance>();
    const { keycloak, initialized } = useKeycloak();
    const kcToken = keycloak?.token ?? '';

    useEffect(() => {
        axiosInstance.current = axios.create({
            baseURL,
            headers: {
                Authorization: initialized ? `Bearer ${kcToken}` : undefined,
            },
        });

        axiosRetry(axiosInstance.current, {
            retries: 3,
            retryCondition: (error) => error.response?.status !== 400 && !error.response,
        });

        const errorHandler = (error: any) => {
            const { response } = error;
            if (response?.status === 403 && keycloak.isTokenExpired()) {
                keycloak.updateToken(50).then(console.log).catch(console.log);
            }
            return Promise.reject({ ...error });
        };

        const successHandler = (response: AxiosResponse<any>) => response;

        axiosInstance.current.interceptors.response.use(
            (response) => successHandler(response),
            (error) => errorHandler(error)
        );

        return () => {
            axiosInstance.current = undefined;
        };
    }, [baseURL, initialized, kcToken, keycloak]);

    return axiosInstance;
};

const HELP_TEXT_DELAY_MS = 60 * 1000 * 3; // 3 minutes
export const useCopySnippet = (
    checkerFn: () => Promise<{}[]>,
    type: string
): [boolean, () => void, boolean, boolean] => {
    const { addToast } = useToasts();
    const axios = useAxios();
    const [copied, setCopied] = useState(false);
    const [snippetDetected, setSnippetDetected] = useState(false);
    const [showHelp, setShowHelp] = useState(false);

    let timeout: NodeJS.Timeout | undefined;
    const handleHelpTimeout = useCallback(
        () => () => {
            if (!snippetDetected) {
                setShowHelp(true);
            }
        },
        [snippetDetected]
    );
    const handleCopyToClipboard = () => {
        setCopied(true);
        timeout = setTimeout(handleHelpTimeout, HELP_TEXT_DELAY_MS);
    };

    const eventChecker = useRef<number>();

    useEffect(() => {
        if (!axios || !axios.current) {
            return;
        }

        clearInterval(eventChecker.current);
        eventChecker.current = window.setInterval(
            () =>
                checkerFn()
                    .then((res) => {
                        if (res && res.length > 0) {
                            setSnippetDetected(true);
                        }
                    })
                    .catch((err) => {
                        clearInterval(eventChecker.current);
                        addToast('Something went wrong. Please reload the page and try again', { appearance: 'error' });
                    }),
            5000
        );
    }, [axios, checkerFn, addToast]);

    useEffect(() => {
        if (snippetDetected) {
            clearInterval(eventChecker.current);
            if (timeout) {
                clearTimeout(timeout);
            }
        }
    }, [snippetDetected, timeout]);

    return [snippetDetected, handleCopyToClipboard, copied, showHelp];
};
